<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { computed } from 'vue'
import type {
  TransactionTransfer,
  TransactionTransferChanges,
} from '@/api/functions/transactions/get-transaction-transfers.action'
import SectionHeader from './SectionHeader.vue'

const props = withDefaults(
  defineProps<{
    transfers?: TransactionTransfer[]
  }>(),
  {
    transfers: () => [],
  }
)

const statusLabels: Record<string, string> = {
  TRANSFERRED: 'منتقل شده',
  BUYER_PAYMENT_PENDING: 'در انتظار پرداخت خریدار',
  SELLER_PAYMENT_PENDING: 'در انتظار پرداخت فروشنده',
  BUYER_PAYMENT_VERIFICATION_PENDING: 'در انتظار بررسی پرداخت خریدار',
  SELLER_PAYMENT_VERIFICATION_PENDING: 'در انتظار بررسی پرداخت فروشنده',
  INITIAL_OWNER_CONFIRMATION_PENDING: 'در انتظار تأیید مالک اولیه',
  PARTY_CONFIRMATION: 'تأیید طرفین',
  IN_PROGRESS: 'در حال انجام',
  FINISHED: 'تکمیل شده',
  CANCELLED: 'لغو شده',
}

const changeLabels: Record<string, string> = {
  seller_bank_account: 'حساب بانکی فروشنده',
  seller_transfer_methods: 'روش‌های انتقال فروشنده',
  seller_bank_account_country: 'کشور حساب بانکی فروشنده',
  buyer_bank_account: 'حساب بانکی خریدار',
  buyer_paypal_bank_account: 'حساب PayPal خریدار',
  buyer_revolut_bank_account: 'حساب Revolut خریدار',
  buyer_transfer_methods: 'روش‌های انتقال خریدار',
  amount: 'مبلغ',
  exchange_rate: 'نرخ تبدیل',
  buyer_discount: 'تخفیف خریدار',
  seller_discount: 'تخفیف فروشنده',
}

const getStatusLabel = (status: string) => {
  return statusLabels[status] || status
}

const getChangesLabel = (changes: TransactionTransferChanges | null) => {
  if (!changes || !Object.keys(changes).length) {
    return 'بدون تغییر'
  }

  return Object.keys(changes)
    .map((key) => changeLabels[key] || key)
    .join('، ')
}

const rows = computed(() =>
  props.transfers.map((item, index) => ({
    index: index + 1,

    fromTransaction: item.fromTransaction.code || '—',
    fromTransactionStatus: getStatusLabel(item.fromTransaction.status),

    toTransaction: item.toTransaction.code || '—',
    toTransactionStatus: getStatusLabel(item.toTransaction.status),

    admin: item.admin.fullName,

    reason: item.reason || 'بدون دلیل ثبت‌شده',

    changes: getChangesLabel(item.changes),

    createdAt: stringToFaDateTime(item.createdAt),
  }))
)

const columns = [
  {
    key: 'index',
    label: '#',
    class: 'col-span-1 font-fa text-sm',
  },
  {
    key: 'fromTransaction',
    label: 'تراکنش مبدا',
    class: 'col-span-2 font-fa text-sm',
    subKey: 'fromTransactionStatus',
    truncate: true,
  },
  {
    key: 'toTransaction',
    label: 'تراکنش مقصد',
    class: 'col-span-2 font-fa text-sm',
    subKey: 'toTransactionStatus',
    truncate: true,
  },
  {
    key: 'admin',
    label: 'انتقال توسط',
    class: 'col-span-2 font-fa text-sm',
    truncate: true,
  },
  {
    key: 'reason',
    label: 'دلیل انتقال',
    class: 'col-span-2 font-fa text-sm pe-6',
    truncate: true,
  },
  {
    key: 'changes',
    label: 'تغییرات',
    class: 'col-span-2 font-fa text-sm pe-6',
    truncate: true,
  },
  {
    key: 'createdAt',
    label: 'تاریخ و زمان',
    class: 'col-span-1 font-fa text-sm',
  },
]
</script>

<template>
    <section class="mt-10">
        <SectionHeader title="تاریخچه انتقال تراکنش" />

        <div class="w-full rounded-xl border border-[#d8d8d8] bg-white p-5 lg:p-6">
            <Table :columns="columns" :rows="rows" />
        </div>
    </section>
</template>